import { jsPDF } from 'jspdf'
import { Document, Packer, Paragraph, TextRun } from 'docx'
import type { Supplier, SupplierMitigationTask, SupplierNews } from '../types'
import { supplierDisplayName } from '../apps/early-signals/supplierRollup'

// Re-import types for the payload (avoid circular deps by using local types for params)
type SupplierLike = Pick<
  Supplier,
  | 'name'
  | 'display_name'
  | 'parent_name'
  | 'procurement_type'
  | 'location'
  | 'industry'
  | 'revenue'
  | 'employees'
  | 'lead_time_days'
  | 'sla_pct'
  | 'quality_pct'
  | 'spend_k'
  | 'months_to_contract_expiry'
  | 'risk_rating'
  | 'stakeholder_score'
  | 'overall_score'
  | 'key_takeaways'
  | 'actions'
  | 'outlook'
>

function sanitizeFilename(name: string): string {
  return name.replace(/[<>:"/\\|?*]/g, '-').replace(/\s+/g, '-').trim() || 'company'
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

function formatSource(source: string | null): string {
  if (!source) return ''
  if (source === 'gnews') return 'GNews'
  if (source === 'excel') return 'Excel'
  return source
}

function exportTitle(supplier: SupplierLike): string {
  return supplierDisplayName(supplier) || supplier.name || 'Supplier'
}

export function exportToPdf(
  supplier: SupplierLike,
  tasks: SupplierMitigationTask[],
  alerts: SupplierNews[]
): void {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' })
  const margin = 20
  const pageWidth = doc.internal.pageSize.getWidth()
  const maxWidth = pageWidth - margin * 2
  let y = 20

  const line = (text: string, opts?: { bold?: boolean; fontSize?: number }) => {
    if (opts?.fontSize) doc.setFontSize(opts.fontSize)
    if (opts?.bold) doc.setFont('helvetica', 'bold')
    const lines = doc.splitTextToSize(text, maxWidth)
    for (const ln of lines) {
      if (y > 270) {
        doc.addPage()
        y = 20
      }
      doc.text(ln, margin, y)
      y += opts?.fontSize === 14 ? 7 : 5
    }
    if (opts?.bold) doc.setFont('helvetica', 'normal')
    if (opts?.fontSize) doc.setFontSize(10)
  }

  const title = exportTitle(supplier)
  doc.setFontSize(14)
  line('Company & Mitigation', { bold: true, fontSize: 14 })
  y += 2
  line(title, { bold: true })
  if (supplier.location) line(supplier.location)
  y += 4

  const hasCompanyDetails =
    supplier.industry != null ||
    supplier.revenue != null ||
    supplier.employees != null ||
    supplier.lead_time_days != null ||
    supplier.sla_pct != null ||
    supplier.quality_pct != null ||
    supplier.spend_k != null ||
    supplier.months_to_contract_expiry != null ||
    supplier.risk_rating != null ||
    supplier.stakeholder_score != null

  if (hasCompanyDetails) {
    line('Company details', { bold: true })
    if (supplier.industry != null && supplier.industry !== '')
      line(`Industry: ${supplier.industry}`)
    if (supplier.revenue != null && supplier.revenue !== '')
      line(`Revenue: ${supplier.revenue}`)
    if (supplier.employees != null && supplier.employees !== '')
      line(`Employees: ${supplier.employees}`)
    if (supplier.lead_time_days != null)
      line(`Lead time (days): ${supplier.lead_time_days}`)
    if (supplier.sla_pct != null) line(`SLA (%): ${supplier.sla_pct}`)
    if (supplier.quality_pct != null) line(`Quality (%): ${supplier.quality_pct}`)
    if (supplier.spend_k != null) line(`Spend ($K): ${supplier.spend_k}`)
    if (supplier.months_to_contract_expiry != null)
      line(`Months to contract expiry: ${supplier.months_to_contract_expiry}`)
    if (supplier.risk_rating != null && supplier.risk_rating !== '')
      line(`Risk rating: ${supplier.risk_rating}`)
    if (supplier.stakeholder_score != null)
      line(`Stakeholder score: ${supplier.stakeholder_score}/5`)
    y += 3
  }

  if (supplier.overall_score != null) {
    line('Risk Rating', { bold: true })
    line(`${supplier.overall_score} / 10`)
    y += 3
  }

  if (alerts.length > 0) {
    line('Company alerts', { bold: true })
    for (const n of alerts) {
      const dateStr = n.published_at || (n as { created_at?: string }).created_at
      const displayDate = dateStr
        ? new Date(dateStr).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })
        : ''
      line(`• ${n.headline}`)
      if (displayDate || n.region || n.source)
        line(`  ${[displayDate, n.region, formatSource(n.source)].filter(Boolean).join(' • ')}`)
    }
    y += 3
  }

  if (supplier.key_takeaways) {
    line('Key Takeaways', { bold: true })
    supplier.key_takeaways
      .split(/\n/)
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((s) => line(`• ${s}`))
    y += 3
  }

  if (supplier.actions) {
    line('Next best action(s)', { bold: true })
    supplier.actions
      .split(/\n/)
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((s) => line(`• ${s}`))
    y += 3
  }

  if (supplier.outlook) {
    line('Risk Outlook / Summary', { bold: true })
    line(supplier.outlook)
    y += 3
  }

  if (tasks.length > 0) {
    line('Tasks', { bold: true })
    for (const t of tasks) {
      line(`• ${t.task} [${t.status}] [${t.priority}]`)
    }
  }

  const filename = `${sanitizeFilename(title)}-company-mitigation.pdf`
  doc.save(filename)
}

export async function exportToWord(
  supplier: SupplierLike,
  tasks: SupplierMitigationTask[],
  alerts: SupplierNews[]
): Promise<void> {
  const children: Paragraph[] = []

  const heading = (text: string) =>
    new Paragraph({
      children: [new TextRun({ text, bold: true })],
      spacing: { after: 200 },
    })
  const para = (text: string) =>
    new Paragraph({ children: [new TextRun(text)], spacing: { after: 100 } })
  const boldPara = (text: string) =>
    new Paragraph({
      children: [new TextRun({ text, bold: true })],
      spacing: { after: 100 },
    })

  const title = exportTitle(supplier)
  children.push(heading('Company & Mitigation'))
  children.push(boldPara(title))
  if (supplier.location) children.push(para(supplier.location))

  const hasCompanyDetails =
    supplier.industry != null ||
    supplier.revenue != null ||
    supplier.employees != null ||
    supplier.lead_time_days != null ||
    supplier.sla_pct != null ||
    supplier.quality_pct != null ||
    supplier.spend_k != null ||
    supplier.months_to_contract_expiry != null ||
    supplier.risk_rating != null ||
    supplier.stakeholder_score != null

  if (hasCompanyDetails) {
    children.push(boldPara('Company details'))
    if (supplier.industry != null && supplier.industry !== '')
      children.push(para(`Industry: ${supplier.industry}`))
    if (supplier.revenue != null && supplier.revenue !== '')
      children.push(para(`Revenue: ${supplier.revenue}`))
    if (supplier.employees != null && supplier.employees !== '')
      children.push(para(`Employees: ${supplier.employees}`))
    if (supplier.lead_time_days != null)
      children.push(para(`Lead time (days): ${supplier.lead_time_days}`))
    if (supplier.sla_pct != null) children.push(para(`SLA (%): ${supplier.sla_pct}`))
    if (supplier.quality_pct != null)
      children.push(para(`Quality (%): ${supplier.quality_pct}`))
    if (supplier.spend_k != null)
      children.push(para(`Spend ($K): ${supplier.spend_k}`))
    if (supplier.months_to_contract_expiry != null)
      children.push(
        para(`Months to contract expiry: ${supplier.months_to_contract_expiry}`)
      )
    if (supplier.risk_rating != null && supplier.risk_rating !== '')
      children.push(para(`Risk rating: ${supplier.risk_rating}`))
    if (supplier.stakeholder_score != null)
      children.push(para(`Stakeholder score: ${supplier.stakeholder_score}/5`))
  }

  if (supplier.overall_score != null) {
    children.push(boldPara('Risk Rating'))
    children.push(para(`${supplier.overall_score} / 10`))
  }

  if (alerts.length > 0) {
    children.push(boldPara('Company alerts'))
    for (const n of alerts) {
      const dateStr = n.published_at || (n as { created_at?: string }).created_at
      const displayDate = dateStr
        ? new Date(dateStr).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })
        : ''
      children.push(para(`• ${n.headline}`))
      if (displayDate || n.region || n.source)
        children.push(
          para(
            `  ${[displayDate, n.region, formatSource(n.source)].filter(Boolean).join(' • ')}`
          )
        )
    }
  }

  if (supplier.key_takeaways) {
    children.push(boldPara('Key Takeaways'))
    supplier.key_takeaways
      .split(/\n/)
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((s) => children.push(para(`• ${s}`)))
  }

  if (supplier.actions) {
    children.push(boldPara('Next best action(s)'))
    supplier.actions
      .split(/\n/)
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((s) => children.push(para(`• ${s}`)))
  }

  if (supplier.outlook) {
    children.push(boldPara('Risk Outlook / Summary'))
    children.push(para(supplier.outlook))
  }

  if (tasks.length > 0) {
    children.push(boldPara('Tasks'))
    for (const t of tasks) {
      children.push(para(`• ${t.task} [${t.status}] [${t.priority}]`))
    }
  }

  const doc = new Document({
    sections: [{ children }],
  })

  const blob = await Packer.toBlob(doc)
  const filename = `${sanitizeFilename(title)}-company-mitigation.docx`
  downloadBlob(blob, filename)
}
